import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'engage.copilotChat.panelWidth';

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function readInitialWidth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const w = parseInt(raw, 10);
      if (!Number.isNaN(w)) return clamp(w, 280, 800);
    }
  } catch {
    /* ignore */
  }
  return 400;
}

const CopilotChatContext = createContext(null);

export function CopilotChatProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelWidth, setPanelWidthState] = useState(readInitialWidth);

  const setPanelWidth = useCallback(value => {
    setPanelWidthState(prev => {
      const max = Math.min(800, Math.floor(window.innerWidth * 0.65));
      const next = typeof value === 'function' ? value(prev) : value;
      const clamped = clamp(next, 280, max);
      try {
        localStorage.setItem(STORAGE_KEY, String(clamped));
      } catch {
        /* ignore */
      }
      return clamped;
    });
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggleOpen: () => setIsOpen(o => !o),
      panelWidth,
      setPanelWidth,
    }),
    [isOpen, panelWidth, setPanelWidth],
  );

  return (
    <CopilotChatContext.Provider value={value}>
      {children}
    </CopilotChatContext.Provider>
  );
}

export function useCopilotChat() {
  const ctx = useContext(CopilotChatContext);
  if (!ctx) {
    throw new Error('useCopilotChat must be used within CopilotChatProvider');
  }
  return ctx;
}
